import { SuperUser } from '@app/user/constants/user-profile.constants';
import { UserState } from '@app/user/models/user-state';
import { createSelector } from '@ngrx/store';
import { SelectedCustomerState } from '@panamax/app-state';
import { DashboardTab } from '@payments/components/dashboard-selector/dashboard-selector.component';
import { TerritoryTile } from '@payments/components/territory-tile/model/territory-tile.model';
import { MAX_INVOICE_SELECTION } from '@payments/constants/constants';
import { AdjustPaymentPageViewModel } from '@payments/models/adjust-payment-page-view-model';
import {
  ARStatementsPageViewModel,
  ARStatementsViewModel,
} from '@payments/models/ar-statements-view.model';
import { MakePaymentPageViewModel } from '@payments/models/make-payment-page-view-model';
import {
  PaymentActivityPageViewModel,
  PaymentViewModel,
} from '@payments/models/payment-activity-page-view-model';
import {
  CustomerSellerMetrics,
  PaymentDashboardPageViewModel,
} from '@payments/models/payment-dashboard-page-view.model';
import {
  BankAccount,
  PaymentPreferencesPageViewModel,
} from '@payments/models/payment-preferences-page-view-model';
import { ArStatementFilterIds } from '@payments/pages/ar-statements/services/ar-statements.service';
import {
  PaymentFilter,
  PaymentFilterIds,
} from '@payments/pages/payment-activity/services/payment-activity.service';
import { PaymentConfirmationPageVM } from '@payments/pages/payment-confirmation/payment-confirmation.page';
import { DashboardFilterIds } from '@payments/pages/payment-dashboard/services/payment-dashboard.service';
import { ReviewPaymentPageVM } from '@payments/pages/review-payment/review-payment.page';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { Customer } from '@usf/customer-types';
import { dsmSelectors } from '@usf/ngrx-order';
import { DsmInfo } from '@usf/ngrx-order/lib/models/client/dsm-info';
import {
  Filter,
  InvoiceFilterIds,
  PaymentCustomer,
  SortFilter,
  Transaction,
  accountSummarySelectors,
  arStatementSelectors,
  bankSelectors,
  invoicesSelectors,
  openInvoicesSelectors,
  paymentCustomerSelectors,
  paymentSelectors,
  recentCreditSelectors,
  statusSummarySelectors,
} from '@usf/ngrx-payment';
import {
  CardColor,
  CustomerBankAccount,
  CustomerBankAccounts,
  CustomerMetrics,
  CustomerReview,
  PaymentActivity,
  PaymentMethod,
  PaymentTransactionsViewModel,
  Status,
  TransactionType,
} from '@usf/payment-types';
import { UserProfile } from '@usf/user-types/user-profile';
import { getCustomers, selectedCustomer } from '../../ngrx-customer/store';
import {
  selectUser,
  selectUserProfiles,
  showInvoiceAndPayments,
} from '../../user/store';
import { PaymentsPageViewModel } from '../models/payments-page-view.model';
import { AllInvoicesPageVM } from '../pages/all-invoices/all-invoices.page';
import { PaymentMetricState } from '@usf/ngrx-payment/lib/models/state/payment-metric.state';

const ALL_CUSTOMERS_NAME = 'Select All';

export interface PaymentCustomersModel {
  customers: number[];
  isSuperUser: boolean;
}

interface UsersAndCustomers {
  userState: UserState;
  customers: Customer[];
  userProfiles: UserProfile[];
  selectedCustomer: Customer;
  paymentCustomer: PaymentCustomer;
}

const getPaymentsUserAndCustomers = createSelector(
  selectUser,
  getCustomers,
  selectUserProfiles,
  selectedCustomer,
  paymentCustomerSelectors.selectPaymentCustomer,
  (
    userState,
    customers,
    userProfiles,
    selectedCustomer,
    paymentCustomer,
  ): UsersAndCustomers => ({
    userState,
    customers,
    userProfiles,
    selectedCustomer,
    paymentCustomer,
  }),
);

const breadcrumbsBase = (
  paymentDashboardLabel = PageTitle.invoicesAndPayments,
) => [
  {
    label: PageTitle.home,
    url: `/${PATHS.HOME}`,
    active: false,
  },
  {
    label: paymentDashboardLabel,
    url: `/${PATHS.PAYMENTS}`,
    active: false,
  },
];

export const getUserTypesThatCanSubmit = createSelector(
  selectUser,
  (userState): boolean => {
    // Customer, Customer Mgmt, Partner Ordering, Internal/Employee
    const userTypesThatCanSubmit = ['1', '2', '3', '4'];
    const canSubmit = userTypesThatCanSubmit.includes(
      userState.user?.userType || '',
    );
    return canSubmit;
  },
);

export const getSelectedPaymentCustomers = createSelector(
  paymentCustomerSelectors.selectPaymentCustomer,
  selectUserProfiles,
  (paymentCustomer, userProfiles): PaymentCustomersModel => {
    return {
      customers: paymentCustomer.customers ?? [],
      isSuperUser: isSuperUser(userProfiles),
    };
  },
);

export const getPaymentsPageViewModel = createSelector(
  getPaymentsUserAndCustomers,
  showInvoiceAndPayments,
  accountSummarySelectors.selectAccountSummary,
  recentCreditSelectors.selectRecentCredits,
  invoicesSelectors.selectInvoices,
  openInvoicesSelectors.selectOpenInvoices,
  selectUserProfiles,
  bankSelectors.selectBank,
  (
    userAndCustomers,
    showInvoiceAndPayments,
    accountSummaryState,
    recentCreditState,
    recentInvoiceState,
    openInvoicesState,
    userProfiles,
    bankDetailsState,
  ) => {
    const customers = userAndCustomers.customers.map(customer => {
      return { ...customer, departmentInd: false };
    });

    const selectedCustomer = getSelectedCustomer(
      userAndCustomers.customers,
      userAndCustomers.paymentCustomer?.customers,
    );
    const credits = recentCreditState.ids?.map(id => {
      const transaction = recentCreditState.entities[id];
      return getPaymentTransactionViewModel(transaction);
    });
    const invoices = recentInvoiceState.ids?.map(id => {
      const transaction = recentInvoiceState.entities[id];
      return getPaymentTransactionViewModel(transaction);
    });

    const dashboardTabs: DashboardTab[] = [
      {
        isActive: true,
        label: 'i18n.payments.dashboardTabs.accountView',
        url: '/payments',
      },
      {
        isActive: false,
        label: 'i18n.payments.dashboardTabs.sellerDashboard',
        url: '/payments/payment-dashboard',
      },
    ];

    const showAutopayBanner =
      !bankDetailsState.loading &&
      bankDetailsState.eligibleADCustomers.length > 0;

    const vm: PaymentsPageViewModel = {
      customers: isSuperUser(userProfiles)
        ? [{ ...userAndCustomers.selectedCustomer, departmentInd: false }]
        : customers,
      selectedCustomer,
      headerCustomer: userAndCustomers.selectedCustomer,
      accountSummaryState,
      canPayInvoice: showInvoiceAndPayments,
      showAllCustomersOption: ['1', '2', '3'].includes(
        userAndCustomers.userState?.user?.userType,
      ),
      credits: credits,
      invoices: invoices,
      isLoadingCredits: recentCreditState.loading,
      isLoadingInvoices: recentInvoiceState.loading,
      countOfOpenInvoices: openInvoicesState.totalOpenInvoices,
      isSuperUser: isSuperUser(userProfiles),
      dashboardTabs,
      showAutopayBanner,
    };
    return vm;
  },
);

export const getAllInvoicesPageViewModel = createSelector(
  showInvoiceAndPayments,
  invoicesSelectors.selectInvoices,
  statusSummarySelectors.selectStatusSummary,
  (
    showInvoiceAndPayments,
    invoiceState,
    statusSummaryState,
  ): AllInvoicesPageVM => {
    const transactions: PaymentTransactionsViewModel[] = invoiceState.ids.map(
      id => {
        const transaction = invoiceState.entities[id];
        return getPaymentTransactionViewModel(
          transaction,
          invoiceState.selectedIds,
        );
      },
    );

    const selectedTransactions: PaymentTransactionsViewModel[] =
      invoiceState.selectedIds.map(id => {
        const transaction = invoiceState.selectedTransactions[id];
        return getPaymentTransactionViewModel(transaction, [id]);
      });

    const breadcrumbs = [
      ...breadcrumbsBase(
        showInvoiceAndPayments
          ? PageTitle.invoicesAndPayments
          : PageTitle.paymentsNoFlag,
      ),
      {
        label: 'All Invoices',
        url: '',
        active: true,
      },
    ];

    const { filters, dateLabel } = mapInvoiceFilters(invoiceState.filters);

    return {
      transactions,
      statusSummary: statusSummaryState,
      hasNextPage: invoiceState.nextPage,
      pageNum: invoiceState.pageNum,
      isLoadingTransaction: invoiceState.loading,
      filters: invoiceState.filters,
      sort: invoiceState.sort,
      filterDateLabel: dateLabel,
      filterCount: countFilters(filters),
      isSelectable: true,
      breadcrumbs,
      selectedTransactions,
      isMaxTransactionsSelected:
        selectedTransactions.length >= MAX_INVOICE_SELECTION,
      payCCAisOn: showInvoiceAndPayments,
      warning: invoiceState.warning,
    };
  },
);

export const getMakePaymentPageViewModel = createSelector(
  invoicesSelectors.selectInvoices,
  statusSummarySelectors.selectStatusSummary,
  (invoiceState, statusSummaryState): MakePaymentPageViewModel => {
    const transactions: PaymentTransactionsViewModel[] = invoiceState.ids.map(
      id => {
        const transaction = invoiceState.entities[id];
        return getPaymentTransactionViewModel(
          transaction,
          invoiceState.selectedIds,
        );
      },
    );

    const selectedTransactions: PaymentTransactionsViewModel[] =
      invoiceState.selectedIds.map(id => {
        const transaction = invoiceState.selectedTransactions[id];
        return getPaymentTransactionViewModel(transaction, [id]);
      });

    const { filters, dateLabel } = mapInvoiceFilters(invoiceState.filters);

    const breadcrumbs = [
      ...breadcrumbsBase(),
      {
        label: PageTitle.makePayment,
        url: '',
        active: true,
      },
    ];

    return {
      transactions,
      statusSummary: statusSummaryState,
      hasNextPage: invoiceState.nextPage,
      pageNum: invoiceState.pageNum,
      isLoadingTransaction: invoiceState.loading,
      filters: invoiceState.filters,
      sort: invoiceState.sort,
      filterDateLabel: dateLabel,
      filterCount: countFilters(filters),
      isSelectable: true,
      breadcrumbs,
      selectedTransactions,
      isMaxTransactionsSelected:
        selectedTransactions.length >= MAX_INVOICE_SELECTION,
      warning: invoiceState.warning,
      canShareDownload: false,
    };
  },
);

export const getFiltersAndSortInformation = createSelector(
  invoicesSelectors.selectInvoices,
  (
    invoiceState,
  ): {
    filters: Filter[];
    sort: SortFilter[];
  } => {
    const filtersSort = {
      filters: invoiceState.filters,
      sort: invoiceState.sort,
    };
    return filtersSort;
  },
);

export const getAdjustPaymentViewModel = createSelector(
  invoicesSelectors.selectInvoices,
  statusSummarySelectors.selectStatusSummary,
  getSelectedPaymentCustomers,
  (invoiceState, statusSummaryState, customer): AdjustPaymentPageViewModel => {
    const breadcrumbs = [
      ...breadcrumbsBase(),
      {
        label: PageTitle.makePayment,
        url: '',
        active: true,
      },
    ];

    const { filters, dateLabel } = mapInvoiceFilters(invoiceState.filters);

    const transactions: PaymentTransactionsViewModel[] = invoiceState.ids.map(
      id => {
        const transaction = invoiceState.entities[id];
        return getPaymentTransactionViewModel(
          transaction,
          invoiceState.selectedIds,
        );
      },
    );

    const selectedTransactions: PaymentTransactionsViewModel[] =
      invoiceState.selectedIds.map(id => {
        const transaction = invoiceState.selectedTransactions[id];
        return getPaymentTransactionViewModel(transaction, [id]);
      });

    const selectedCustomers = selectedTransactions
      .map(transaction => transaction.customerNumber)
      .filter((value, index, array) => array.indexOf(value) === index);

    const totalAmount = selectedTransactions.reduce(
      (prev, curr) => +prev + (+curr.paymentAmount || 0),
      0,
    );

    const initialSelectedTransactions = Object.values(
      invoiceState.initialSelectedTransactions,
    ).map(transaction => {
      return getPaymentTransactionViewModel(
        transaction,
        invoiceState.selectedIds,
      );
    });

    return {
      breadcrumbs,
      filterCount: countFilters(filters),
      filterDateLabel: dateLabel,
      filters: invoiceState.filters,
      initialSelectedTransactions,
      isLoadingTransaction: invoiceState.loading,
      isMaxTransactionsSelected:
        selectedTransactions.length >= MAX_INVOICE_SELECTION,
      isSelectable: true,
      hasNextPage: invoiceState.nextPage,
      pageNum: invoiceState.pageNum,
      selectedCustomers:
        selectedCustomers.length > 0 ? selectedCustomers : customer.customers,
      selectedTransactions,
      sort: invoiceState.sort,
      statusSummary: statusSummaryState,
      totalAmount,
      transactions: transactions,
      translatedIds: invoiceState.translatedIds,
    };
  },
);

export const getReviewPaymentViewModel = createSelector(
  selectUser,
  invoicesSelectors.selectInvoices,
  bankSelectors.selectBank,
  paymentSelectors.selectPayment,
  getUserTypesThatCanSubmit,
  (
    userState,
    invoiceState,
    bankDetailsState,
    paymentState,
    canSubmit,
  ): ReviewPaymentPageVM => {
    const breadcrumbs = [
      ...breadcrumbsBase(),
      {
        label: PageTitle.makePayment,
        url: '',
        active: true,
      },
    ];

    const transactions: PaymentTransactionsViewModel[] =
      invoiceState.selectedIds.map(id => {
        const transaction = invoiceState.selectedTransactions[id];
        return getPaymentTransactionViewModel(transaction, [id]);
      });

    let overallCustomerBankAccounts: CustomerBankAccount[] = [];
    let nonAutoPayTransactionCount = 0;

    const customers = Array.from(
      transactions
        .reduce((prev, curr) => {
          const key = curr.customerNumber;
          if (!prev.has(key)) {
            prev.set(key, {
              customerName: curr.customerName,
              customerNumber: curr.customerNumber,
              billToID: curr.billToID,
              transactions: [],
              autoPayTransactions: [],
              customerBankAccounts: [],
              isAllAutoPay: false,
            } as CustomerReview);
          }

          if (prev.get(key).customerBankAccounts.length === 0) {
            const customerBankAccounts =
              bankDetailsState.customerBankDetails.find(
                customerBankDetail => customerBankDetail.customerNumber === key,
              )?.bankDetails || [];
            prev.get(key).customerBankAccounts = customerBankAccounts;

            const selectedBankAccount =
              bankDetailsState.selectedBankAccounts?.find(
                customerBankDetail => customerBankDetail.customerNumber === key,
              );

            if (selectedBankAccount) {
              prev.get(key).selectedBankAccount = selectedBankAccount
                ? { ...selectedBankAccount, customerNumber: key }
                : prev.get(key).selectedBankAccount;
              if (selectedBankAccount.paymentMethod === 'AD') {
                prev.get(key).autoPaySelected = true;
              }
            } else {
              const primaryBankAccount = customerBankAccounts.find(
                bankDetail => bankDetail.isPrimary,
              );

              if (primaryBankAccount) {
                prev.get(key).selectedBankAccount = {
                  ...primaryBankAccount,
                  customerNumber: key,
                };
                if (primaryBankAccount?.paymentMethod === 'AD') {
                  prev.get(key).autoPaySelected = true;
                }
              }
            }
          }

          if (checkAutoPay(prev.get(key), curr)) {
            prev.get(key).autoPayTransactions.push(curr);
          } else {
            nonAutoPayTransactionCount += 1;
            prev.get(key).transactions.push(curr);
          }
          return prev;
        }, new Map<number, CustomerReview>())
        .values(),
    );

    let nonAutoPayCustomerCount = 0;
    customers.forEach(customer => {
      if (
        customer.transactions.length === 0 &&
        customer.autoPayTransactions.length > 0
      ) {
        customer.isAllAutoPay = true;
      } else {
        nonAutoPayCustomerCount += 1;
      }

      overallCustomerBankAccounts =
        customer.selectedBankAccount && !customer.isAllAutoPay
          ? [...overallCustomerBankAccounts, customer.selectedBankAccount]
          : overallCustomerBankAccounts;
    });

    customers.sort((a, b) => {
      if (!a.isAllAutoPay && b.isAllAutoPay) {
        return -1;
      } else if (a.isAllAutoPay && !b.isAllAutoPay) {
        return 1;
      } else {
        return a.customerName.localeCompare(b.customerName);
      }
    });

    if (overallCustomerBankAccounts.length) {
      overallCustomerBankAccounts = overallCustomerBankAccounts.filter(
        (account, index, array) => array.indexOf(account) === index,
      );
    }

    const autoPayCustomerNumbers = customers
      .filter(
        c => c.transactions.length > 0 && c.autoPayTransactions.length > 0,
      )
      .map(c => c.customerNumber);
    const totalAmount = customers.reduce(
      (prev, curr) =>
        prev + curr.transactions.reduce((p, c) => p + c.paymentAmount || 0, 0),
      0,
    );

    const customersHaveBankAccounts = customers.every(
      customer =>
        (customer.transactions.length > 0 && customer.selectedBankAccount) ||
        customer.isAllAutoPay,
    );
    const isSubmitEnabled =
      canSubmit && totalAmount > 0 && customersHaveBankAccounts;

    return {
      autoPayCustomerNumbers,
      breadcrumbs,
      currentPayment: paymentState.customerPayments,
      customers,
      isBanksEditable: canSubmit,
      isSubmitEnabled,
      nonAutoPayCustomerCount,
      nonAutoPayTransactionCount,
      overallCustomerBankAccounts,
      transactions,
      submitError: paymentState.error,
      bankAccountError: bankDetailsState.error,
      totalAmount,
      isLoading:
        userState.loading ||
        invoiceState.loading ||
        bankDetailsState.loading ||
        paymentState.loading,
    };
  },
);

export const getPaymentConfirmationViewModel = createSelector(
  getReviewPaymentViewModel,
  invoicesSelectors.selectInvoices,
  (vm, invoice): PaymentConfirmationPageVM => {
    const transactions = vm.transactions.filter(
      transaction => !transaction.hasError,
    );
    const transactionsWithErrors = vm.transactions.filter(
      transaction => transaction.hasError,
    );
    const totalAmountErrorTransactions = transactionsWithErrors.reduce(
      (totalAmount, transaction) => totalAmount + transaction.paymentAmount,
      0,
    );
    const customers = vm.customers.filter(customer =>
      transactions.some(
        transaction => transaction.customerNumber === customer.customerNumber,
      ),
    );
    let postingDate = 'NA';
    const dateString = invoice.postingDate?.toString();
    if (dateString != '0') {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);
      postingDate = `${parseInt(month)}/${parseInt(day)}/${year}`;
    }
    return {
      totalAmount: vm.totalAmount - totalAmountErrorTransactions,
      breadcrumbs: vm.breadcrumbs,
      selectedCustomersAmount: vm.customers.length,
      customers,
      isLoading: vm.isLoading,
      overallCustomerBankAccounts: vm.overallCustomerBankAccounts,
      transactions,
      transactionsWithErrors,
      postingDate,
    };
  },
);

export const getPaymentFiltersAndSortInformation = createSelector(
  paymentSelectors.selectPaymentActivity,
  (
    paymentActivityState,
  ): {
    filters: PaymentFilter[];
    sort: SortFilter[];
  } => {
    const filtersSort = {
      filters: paymentActivityState.filters.map(removeCreditCardBrands),
      sort: paymentActivityState.sort,
    };
    return filtersSort;
  },
);

export const getPaymentActivityPageViewModel = createSelector(
  showInvoiceAndPayments,
  paymentSelectors.selectPayment,
  paymentSelectors.selectPaymentActivity,
  statusSummarySelectors.selectPaymentActivityStatusSummary,
  getSelectedPaymentCustomers,
  (
    showInvoiceAndPayments,
    paymentStore,
    paymentActivityStore,
    statusSummary,
    selectedPaymentCustomers,
  ): PaymentActivityPageViewModel => {
    const breadcrumbs = [
      ...breadcrumbsBase(
        showInvoiceAndPayments
          ? PageTitle.invoicesAndPayments
          : PageTitle.paymentsNoFlag,
      ),
      {
        label: PageTitle.paymentActivity,
        url: '',
        active: true,
      },
    ];

    const { filters, dateLabel } = mapPaymentFilters(
      paymentActivityStore.filters,
    );

    const payments: PaymentViewModel[] =
      paymentActivityStore.paymentActivities.map(payment =>
        getPaymentViewModel(payment),
      );

    return {
      payments,
      statusSummary,
      filters,
      sort: paymentActivityStore.sort,
      filterDateLabel: dateLabel,
      filterCount: countFilters(filters.map(removeCreditCardBrands)),
      hasNextPage: paymentActivityStore.hasNextPage,
      pageNum: paymentActivityStore.pageNum,
      breadcrumbs,
      isLoadingPayments: paymentStore.loading,
      isLoadingActivity: paymentActivityStore.loading,
      warning: paymentActivityStore.warning,
    };
  },
);

export const getPaymentPreferencesPageViewModel = createSelector(
  bankSelectors.selectBank,
  getCustomers,
  getUserTypesThatCanSubmit,
  (
    bankDetailsState,
    customers,
    userCanModifyPaymentPreferences,
  ): PaymentPreferencesPageViewModel => {
    const breadcrumbs = [
      ...breadcrumbsBase(),
      {
        label: PageTitle.paymentPreferences,
        url: '',
        active: true,
      },
    ];

    const { autopayBankAccounts, manualDeductBankAccounts } = mapBankAccounts(
      bankDetailsState.customerBankDetails,
      customers,
    );

    const isEligleAutopayCustomers =
      !bankDetailsState.loading &&
      bankDetailsState.eligibleADCustomers.length > 0;

    return {
      autopayBankAccounts,
      breadcrumbs,
      eligibleADCustomers: bankDetailsState.eligibleADCustomers,
      customersCount: bankDetailsState.customerBankDetails.length,
      isLoading: bankDetailsState.loading,
      enrolledADCustomersResponse: bankDetailsState.enrolledADCustomersResponse,
      manualDeductBankAccounts,
      userCanModifyPaymentPreferences,
      isEligleAutopayCustomers,
    };
  },
);

export const getPaymentDashboardPageViewModel = createSelector(
  selectUser,
  selectUserProfiles,
  paymentSelectors.selectPaymentMetricState,
  dsmSelectors.selectDsmInfo,
  getCustomers,
  (
    _userState,
    userProfiles,
    paymentMetricState,
    dsmInfoState,
    allCustomers,
  ): PaymentDashboardPageViewModel => {
    const dashboardTabs: DashboardTab[] = [
      {
        isActive: false,
        label: 'i18n.payments.dashboardTabs.accountView',
        url: '/payments',
      },
      {
        isActive: true,
        label: 'i18n.payments.dashboardTabs.sellerDashboard',
        url: '/payments/payment-dashboard',
      },
    ];
    const territoryTiles: TerritoryTile[] = [
      {
        color: 'red',
        id: 'creditHolds',
        action: {
          query: {
            filterName: DashboardFilterIds.creditStatus,
            value: ['On Hold', 'Soft Hold'],
          },
        },
        title: 'i18n.payments.paymentDashboard.territoryTiles.creditHolds',
        value: paymentMetricState?.metricSummary.creditHolds || 0,
      },
      {
        color: 'red',
        id: 'pastTerms',
        action: {
          query: {
            filterName: DashboardFilterIds.pastTerms,
            value: '',
          },
        },
        title: 'i18n.payments.paymentDashboard.territoryTiles.pastTerms',
        value: paymentMetricState?.metricSummary.pastTerms || 0,
      },
      {
        color: 'yellow',
        id: 'closeToTerms',
        action: {
          query: {
            filterName: DashboardFilterIds.closeToTerms,
            value: '',
          },
        },
        title: 'i18n.payments.paymentDashboard.territoryTiles.closeToTerms',
        value: paymentMetricState?.metricSummary.closeToTerms || 0,
      },
      {
        id: 'current',
        title: 'i18n.payments.paymentDashboard.territoryTiles.current',
        value:
          (Math.round(paymentMetricState?.metricSummary.currentPercentage) ||
            0) + '%',
        action: {
          query: {
            filterName: DashboardFilterIds.currentPercentageBelow100,
            value: '',
          },
        },
      },
      {
        color: 'red',
        id: 'overdueInvoices',
        action: {
          page: PATHS.PAYMENTS_ALL_INVOICES,
          query: {
            [InvoiceFilterIds.Customers]: '*',
            [InvoiceFilterIds.Status]: `${Status.OVERDUE},${Status.PARTIALLY_PAID}`,
          },
        },
        title: 'i18n.payments.paymentDashboard.territoryTiles.overdueInvoices',
        value: paymentMetricState?.metricSummary.overDueInvoicesTotal || 0,
      },
      {
        color: 'green',
        id: 'scheduledPayments',
        action: {
          page: PATHS.PAYMENTS_PAYMENT_ACTIVITY,
          query: {
            [PaymentFilterIds.customers]: '*',
            [PaymentFilterIds.status]: `${Status.PENDING},${Status.PROCESSING}`,
          },
        },
        title:
          'i18n.payments.paymentDashboard.territoryTiles.scheduledPayments',
        value: paymentMetricState?.metricSummary.scheduledPaymentsTotal || 0,
      },
      {
        color: 'green',
        id: 'unusedCredits',
        action: {
          page: PATHS.PAYMENTS_ALL_INVOICES,
          query: {
            [InvoiceFilterIds.Customers]: '*',
            [InvoiceFilterIds.Status]: `${Status.UNUSED},`,
          },
        },
        title: 'i18n.payments.paymentDashboard.territoryTiles.unusedCredits',
        value: paymentMetricState?.metricSummary.unusedCreditsTotal || 0,
      },
    ];

    const customerSellers = getCustomerSellers(
      allCustomers,
      dsmInfoState.dsmInfo,
    );
    const customerSellerMetrics: CustomerSellerMetrics[] =
      mapMetricsWithSellerInitials(
        paymentMetricState.customerMetrics,
        dsmInfoState.dsmInfo,
        customerSellers,
      );

    const customersToSellersMap: Map<number, string[]> = new Map<
      number,
      string[]
    >();
    for (let [customerNumber, sellers] of customerSellers) {
      const filteredSellers = Array.from(sellers).filter(seller => !!seller);
      customersToSellersMap.set(customerNumber, filteredSellers);
    }

    return {
      customerMetrics: customerSellerMetrics,
      dsmInfo: dsmInfoState.dsmInfo,
      dashboardTabs,
      isLoading: paymentMetricState.loading,
      isSuperUser: isSuperUser(userProfiles),
      metricSummary: paymentMetricState.metricSummary, //TODO: Unused, waiting for tracking stories if not remove it
      territoryTiles,
      hasReachedCustomerLimit: !!paymentMetricState.customerMetricsError,
      customerSellers: customersToSellersMap,
    };
  },
);

const mapMetricsWithSellerInitials = (
  customerMetrics: CustomerMetrics[],
  dsmInfo: DsmInfo[],
  customerSellers: Map<number, Set<string>>,
): CustomerMetrics[] => {
  return customerMetrics.map(customer => {
    const seller = getSeller(dsmInfo, customer);
    let multiCustomer = customerSellers.get(customer.customerNumber)?.size > 1;
    let pillContents: string;
    if (multiCustomer) {
      pillContents = 'i18n.payments.paymentDashboard.multiCustomer';
    } else {
      pillContents = !!seller
        ? seller?.firstName.substring(0, 1) + seller?.lastName.substring(0, 1)
        : '';
    }
    return {
      ...customer,
      seller: pillContents,
      allSellers: Array.from(
        customerSellers.get(customer.customerNumber) ?? [],
      ),
    };
  });
};

const getCustomerSellers = (
  allCustomers: Customer[],
  dsmInfo: DsmInfo[],
): Map<number, Set<string>> => {
  const customerSellers = new Map<number, Set<string>>();

  dsmInfo.forEach(dsm => {
    const sellerName = `${dsm.firstName} ${dsm.lastName}`;

    allCustomers.forEach(customer => {
      if (
        dsm.salesRoutes.find(route => route.salesRoute === customer.salesRoute)
      ) {
        const sellersForCustomer =
          customerSellers.get(customer.customerNumber) ?? new Set<string>();
        sellersForCustomer.add(sellerName);
        customerSellers.set(customer.customerNumber, sellersForCustomer);
      }
    });
  });

  return customerSellers;
};

export const getArStatementFiltersAndSortInformation = createSelector(
  arStatementSelectors.selectArStatements,
  (
    arStatementState,
  ): {
    filters: Filter[];
    sort: SortFilter[];
  } => {
    const filtersSort = {
      filters: arStatementState.filters,
      sort: arStatementState.sort,
    };
    return filtersSort;
  },
);

export const getARStatementsPageViewModel = createSelector(
  showInvoiceAndPayments,
  arStatementSelectors.selectArStatements,
  selectUser,
  (
    showInvoiceAndPayments,
    arStatementState,
    _userState,
  ): ARStatementsPageViewModel => {
    const breadcrumbs = [
      ...breadcrumbsBase(
        showInvoiceAndPayments
          ? PageTitle.invoicesAndPayments
          : PageTitle.paymentsNoFlag,
      ),
      {
        label: PageTitle.arStatements,
        url: '',
        active: true,
      },
    ];

    const { filters, dateLabel } = mapInvoiceFilters(arStatementState.filters);
    const arStatements: ARStatementsViewModel[] = arStatementState.ids?.map(
      id => ({
        ...arStatementState.entities[id],
        selected: arStatementState.selectedIds.includes(id),
      }),
    );

    return {
      breadcrumbs,
      isInitialLoading: arStatementState.loading && arStatements.length === 0,
      filterCount: countFilters(filters),
      filterDateLabel: dateLabel,
      filters: arStatementState.filters,
      isLoading: arStatementState.loading,
      sort: arStatementState.sort,
      arStatements,
      countStatementSelected: arStatements.filter(
        statement => statement?.selected,
      ).length,
      nextPage: arStatementState.nextPage,
      pageNum: arStatementState.pageNum,
      isPayCCAOn: showInvoiceAndPayments,
    };
  },
);

const mapBankAccounts = (
  bankAccountsPerCustomer: CustomerBankAccounts[],
  customers: Customer[],
): {
  autopayBankAccounts: BankAccount[];
  manualDeductBankAccounts: BankAccount[];
} => {
  return Array.from(
    new Map(
      bankAccountsPerCustomer.map(bankAccount => [
        bankAccount.customerNumber,
        bankAccount,
      ]),
    ).values(),
  ).reduce(
    (prev, bankAccountsPerCustomer) => {
      const customerName = customers.find(
        customer =>
          customer.customerNumber === bankAccountsPerCustomer.customerNumber,
      )?.customerName;

      const autopayAccountsPerCustomer: BankAccount[] =
        bankAccountsPerCustomer.bankDetails
          .filter(bankDetail => bankDetail.paymentMethod === 'AD')
          .map(bankDetail => ({
            ...bankDetail,
            customerName,
            customerNumber: bankAccountsPerCustomer.customerNumber,
            autoDeductStartDateFormatted: bankDetail.autoDeductStartDate
              ? formatIssueDate(bankDetail.autoDeductStartDate)
              : '',
          }));
      const manualDeductAccountsPerCustomer: BankAccount[] =
        bankAccountsPerCustomer.bankDetails
          .filter(bankDetail => bankDetail.paymentMethod !== 'AD')
          .map(bankDetail => ({
            ...bankDetail,
            customerName,
            customerNumber: bankAccountsPerCustomer.customerNumber,
            autoDeductStartDateFormatted: bankDetail.autoDeductStartDate
              ? formatIssueDate(bankDetail.autoDeductStartDate)
              : '',
          }));
      return {
        autopayBankAccounts: [
          ...prev.autopayBankAccounts,
          ...autopayAccountsPerCustomer,
        ],
        manualDeductBankAccounts: [
          ...prev.manualDeductBankAccounts,
          ...manualDeductAccountsPerCustomer,
        ],
      };
    },
    { autopayBankAccounts: [], manualDeductBankAccounts: [] },
  );
};

const isSuperUser = (userProfiles: UserProfile[]) => {
  const superUserProfile = userProfiles?.find(
    userProf => userProf.key === SuperUser,
  );
  return !!superUserProfile ? superUserProfile.value === 'Y' : false;
};

export const getSelectedFilterCustomers = createSelector(
  invoicesSelectors.selectInvoices,
  getCustomers,
  (invoiceState, customers): any => {
    let filteredCustomers;
    filteredCustomers = invoiceState.filters.find(
      o => o.filterName === 'customers',
    )
      ? invoiceState.filters.find(o => o.filterName === 'customers').value !==
        null
        ? invoiceState.filters.find(o => o.filterName === 'customers').value
        : []
      : [];
    if (
      filteredCustomers.find(f => {
        f.customerName !== 'Select All' || f.customerName !== 'Deselect All';
      })
    ) {
      filteredCustomers.splice(
        filteredCustomers.findIndex(f => {
          f.customerName !== 'Select All' || f.customerName !== 'Deselect All';
        }),
        1,
      );
    }
    const selectedCustomer = getSelectedCustomer(customers, filteredCustomers);
    return selectedCustomer;
  },
);

const transactionTypeStringShort = (
  transactionType: TransactionType,
): string => {
  switch (transactionType) {
    case TransactionType.payment:
      return 'PT';
    case TransactionType.INV:
      return 'IN';
    case TransactionType.credit:
      return 'CR';
    case TransactionType.debit:
      return 'DB';
    case TransactionType.service_Fee:
      return 'SV';
    case TransactionType.discontinued_when_out:
      return 'DW';
    case TransactionType.INVOICE:
      return 'IN';
    case TransactionType.PREDELIVERY_INVOICE:
      return 'IN';
    case TransactionType.INVOICE_WILL_CALL:
      return 'WC';
    case TransactionType.INVOICE_VENDOR_SHIP:
      return 'VS';
    case TransactionType.INVOICE_CHEF_STORE:
      return 'CS';
    case TransactionType.CREDIT_MEMO:
      return 'CR';
    case TransactionType.CREDIT_MEMO_SUMMARY:
      return 'CR';
    case TransactionType.CRE_SHORT:
      return 'CR';
    case TransactionType.DEBIT_MEMO:
      return 'DB';
    case TransactionType.DEBIT_MEMO_SUMMARY:
      return 'DB';
    case TransactionType.DEB_SHORT:
      return 'DB';
    case TransactionType.OTHER:
      return 'OT';
  }
};

export const statusString = (status: Status): string => {
  return status?.replace(/_/g, ' ');
};

export const getPaymentMethod = (rawPaymentMethod: string): PaymentMethod => {
  if (
    Object.values(PaymentMethod).includes(rawPaymentMethod as PaymentMethod)
  ) {
    if (
      rawPaymentMethod === PaymentMethod.AMEX ||
      rawPaymentMethod === PaymentMethod.AMX
    ) {
      return PaymentMethod.CC;
    } else {
      return rawPaymentMethod as PaymentMethod;
    }
  } else {
    if (!isNaN(+rawPaymentMethod)) return PaymentMethod.CHECK;
    return PaymentMethod.OTHER;
  }
};

const formatIssueDate = (issueDate: any): string => {
  return (
    issueDate.toString().substring(4, 6) +
    '/' +
    issueDate.toString().substring(6, 8) +
    '/' +
    issueDate.toString().substring(0, 4)
  );
};

const getSelectedCustomer = (
  customers: Customer[],
  customerNumber: number[],
): SelectedCustomerState[] => {
  const customer = customers?.filter(c =>
    customerNumber?.includes(c.customerNumber),
  );
  if (customer.length === 0) {
    return [
      {
        customerName: ALL_CUSTOMERS_NAME,
        customerNumber: 0,
        departmentNumber: 0,
        departmentInd: false,
        divisionNumber: 0,
      },
    ];
  }
  const selectedCustomers = customer.map(customer => {
    return {
      customerName: customer.customerName,
      customerNumber: customer.customerNumber,
      departmentNumber: 0,
      divisionNumber: 0,
      departmentInd: false,
    };
  });
  return selectedCustomers;
};

const convertDate = (dateNumber: number): Date => {
  if (dateNumber !== 0) {
    let dateString = `${dateNumber}`;
    dateString = `${dateString.substring(0, 4)}-${dateString.substring(
      4,
      6,
    )}-${dateString.substring(6, 8)}`;
    return new Date(dateString);
  } else {
    return null;
  }
};

export const isValidTransactionNumber = (x: string) => {
  return x !== '0' && !!x;
};

export const getPaymentTransactionViewModel = (
  transaction: Transaction,
  selectedIds?: string[],
): PaymentTransactionsViewModel => {
  const isr =
    !!transaction.isr && !!transaction.isr.docID ? transaction.isr : null;
  const isPayable =
    transaction?.netAmount > 0 &&
    (transaction?.status === Status.UNPAID ||
      transaction?.status === Status.OVERDUE ||
      transaction?.status === Status.PARTIALLY_PAID);

  return {
    _id: transaction._id,
    customerNumber: transaction?.customerNumber,
    customerName: transaction?.customerName,
    customerPO: transaction?.customerPO,
    amount: transaction?.grossAmount,
    amountDue: transaction?.netAmount,
    billToID: transaction.tandem.billToID,
    paymentAmount:
      transaction.paymentAmount !== null &&
      transaction.paymentAmount !== undefined
        ? transaction.paymentAmount
        : transaction?.netAmount,
    transactionTypeString: transaction?.transactionDescription,
    transactionTypeStringShort: transaction?.isr?.docType
      ? transactionTypeStringShort(
          TransactionType[
            Object.keys(TransactionType)[
              Object.values(TransactionType).indexOf(
                transaction.isr.docType as unknown as TransactionType,
              )
            ]
          ],
        )
      : transactionTypeStringShort(transaction?.transactionType),
    transactionType: transaction?.transactionType,
    statusString: statusString(transaction?.status),
    status: transaction?.status,
    orderNumber: transaction?.orderNumber,
    statusColor: getStatusColor(transaction?.status),
    issueDate: convertDate(transaction?.issueDate),
    dueDate: convertDate(transaction?.dueDate),
    selected: selectedIds?.includes(transaction._id) || false,
    isr: isr,
    isPayable: isPayable,
    hasError: transaction.hasError || false,
    errorCode: transaction.errorCode,
    paymentActivityID: transaction?.payment?.paymentActivityID,
    ...getTransactionNumbers(transaction),
  };
};

const getStatusColor = (status: Status): CardColor => {
  switch (status) {
    case Status.OVERDUE:
    case Status.PARTIALLY_PAID:
    case Status.PENDING:
    case Status.UNPAID:
    case Status.PROCESSING:
    case Status.CANCELLING:
      return 'yellow';
    case Status.CANCELLED:
    case Status.CANCELLING:
      return 'red';
    default:
      return 'green';
  }
};

export const getTransactionNumbers = ({
  originalTransaction,
  secondaryTransaction,
}): {
  hasSecondaryTransaction: boolean;
  transactionNumber: string;
  secondaryTransactionNumber: string;
} => {
  const hasSecondaryTransaction =
    isValidTransactionNumber(secondaryTransaction) &&
    isValidTransactionNumber(originalTransaction);
  return {
    hasSecondaryTransaction,
    transactionNumber:
      hasSecondaryTransaction || isValidTransactionNumber(originalTransaction)
        ? originalTransaction
        : secondaryTransaction,
    secondaryTransactionNumber: hasSecondaryTransaction
      ? secondaryTransaction
      : '',
  };
};

export const getPaymentViewModel = (
  paymentActivity: PaymentActivity,
): PaymentViewModel => {
  return {
    ...paymentActivity,
    paymentDate: convertDate(paymentActivity.paymentDate),
    paymentMethod: getPaymentMethod(paymentActivity.paymentMethod),
  };
};

const mapInvoiceFilters = (invoiceFilters: Filter[]) => {
  const filters = invoiceFilters;
  let dateLabel = '';
  if (
    filters.length &&
    filters.find(f => f.filterName === InvoiceFilterIds.FromIssueDate)?.value &&
    filters.find(f => f.filterName === InvoiceFilterIds.ToIssueDate)?.value
  ) {
    const fromIssueDate = filters.find(
      f => f.filterName === InvoiceFilterIds.FromIssueDate,
    ).value;
    const toIssueDate = filters.find(
      f => f.filterName === InvoiceFilterIds.ToIssueDate,
    ).value;
    dateLabel =
      formatIssueDate(fromIssueDate) + ' - ' + formatIssueDate(toIssueDate);
  } else if (
    filters.length &&
    filters.find(f => f.filterName === ArStatementFilterIds.fromStatementDate)
      ?.value &&
    filters.find(f => f.filterName === ArStatementFilterIds.toStatementDate)
      ?.value
  ) {
    const fromStatementDate = filters.find(
      f => f.filterName === ArStatementFilterIds.fromStatementDate,
    ).value;
    const toStatementDate = filters.find(
      f => f.filterName === ArStatementFilterIds.toStatementDate,
    ).value;
    dateLabel =
      formatIssueDate(fromStatementDate) +
      ' - ' +
      formatIssueDate(toStatementDate);
  }
  return { filters, dateLabel };
};

const mapPaymentFilters = (paymentFilters: Filter[]) => {
  const filters = paymentFilters;
  let dateLabel = '';
  if (
    filters.length &&
    filters.find(f => f.filterName === PaymentFilterIds.fromPaymentDate)
      ?.value &&
    filters.find(f => f.filterName === PaymentFilterIds.toPaymentDate)?.value
  ) {
    const fromIssueDate = filters.find(
      f => f.filterName === PaymentFilterIds.fromPaymentDate,
    ).value;
    const toIssueDate = filters.find(
      f => f.filterName === PaymentFilterIds.toPaymentDate,
    ).value;
    dateLabel =
      formatIssueDate(fromIssueDate) + ' - ' + formatIssueDate(toIssueDate);
  }
  return { filters, dateLabel };
};

const countFilters = (filters: Filter[]) => {
  return filters.reduce((count, filter) => {
    if (
      filter.filterName === InvoiceFilterIds.ToIssueDate ||
      filter.filterName === InvoiceFilterIds.ExcludeInvoices ||
      filter.filterName === PaymentFilterIds.toPaymentDate
    ) {
      return count;
    }
    if (Array.isArray(filter.value)) {
      return count + filter.value.length;
    } else if (!!filter.value) {
      return count + 1;
    }
  }, 0);
};

const removeCreditCardBrands = (filter: Filter) => ({
  ...filter,
  value:
    filter.filterName === PaymentFilterIds.paymentMethod
      ? (filter.value as string[]).filter(
          values =>
            values !== PaymentMethod.AMEX && values !== PaymentMethod.AMX,
        )
      : filter.value,
});

const checkAutoPay = (
  customer: CustomerReview,
  transaction: PaymentTransactionsViewModel,
) => {
  if (!customer.selectedBankAccount) {
    return false;
  }

  const dueDate = +transaction.issueDate
    .toJSON()
    .replace(/-/g, '')
    .substring(0, 8);
  const autoDeductEndDate =
    customer.selectedBankAccount.autoDeductEndDate || 99999999;
  if (
    customer.autoPaySelected &&
    dueDate >= customer.selectedBankAccount.autoDeductStartDate &&
    dueDate <= autoDeductEndDate
  ) {
    return true;
  }
  return false;
};

const getSeller = (
  dsmInfo: DsmInfo[],
  customer: CustomerMetrics,
): DsmInfo | undefined => {
  return dsmInfo.find(dsm =>
    dsm.customers.find(c => c.customerNumber === customer.customerNumber),
  );
};
